import { Box, Flex, Skeleton } from "@chakra-ui/react";
import { CellClassParams, ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import { MrFilterSetting } from "../../../../api/settings";
import type { Suspect } from "../../../../api/suspects";
import { EhrDetails } from "../../../../domain/ehr";
import {
  MappedConsolidatedResources,
  PatientQueryParamProps,
} from "../../shared-logic/consolidated-context/reducer";
import { useSharedContext } from "../../shared-logic/shared-context";
import { METRIPORT_PRIMARY_FADED } from "../../shared-logic/style";
import { useDocumentMenu } from "../../shared-logic/useDocumentMenu";
import useMetriportToast from "../../shared-logic/useMetriportToast";
import { NoPatientData } from "../no-data";
import { SidePanel } from "../side-panel/side-panel";
import RawDocumentsSection from "./documents/fhir-section";
import { FhirSectionProps } from "./fhir-section";
import { useFhirSections } from "../../shared-logic/useFhirSections";

export type SectionCounts = {
  sectionCounts: {
    reports: number;
    documents: number;
  };
};

export type EhrActions = EhrDetails & {
  writingIds: string[];
  failedIds: { id: string; error: unknown }[];
  successIds: string[];
  setWritingIds: React.Dispatch<React.SetStateAction<string[]>>;
  setFailedIds: React.Dispatch<React.SetStateAction<{ id: string; error: unknown }[]>>;
  setSuccessIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export type GenerateTableDataParams = {
  bundle: MappedConsolidatedResources | undefined;
  tableFilters: MrFilterSetting | undefined;
  ehrActions?: EhrActions;
  isMetriportUser?: boolean;
  suspects?: Suspect[];
};

export type GenerateTableData = ({ bundle, tableFilters, ehrActions }: GenerateTableDataParams) => {
  columnDefs: ColDef[];
  rowData: object[];
  alternativeRowData?: object[];
};

export type FhirSections = {
  title: string;
  key: string;
  component?: React.ComponentType<Omit<FhirSectionProps, "defaultColDef">>;
  generateTableData?: GenerateTableData;
  sidePanelContent?: React.ElementType;
}[];

export type FhirSectionActions = {
  setSidePanelContent: (content: React.ReactNode) => void;
  handleDocumentClick: (
    file: { id: string; fileName: string; fileType: string },
    event: React.PointerEvent<HTMLElement>
  ) => void;
};

const DefaultSidePanelContent: React.FC<{
  isImpersonating?: boolean;
  mappedConsolidated?: MappedConsolidatedResources;
  [key: string]: unknown;
}> = () => null;

export function PatientFHIRData({
  filters,
  mappedConsolidated,
  isConsolidatedLoading,
  patientQueryParams,
  isImpersonating,
  usingMetriportOnlyBundleFromConsolidated = false,
  setSidePanelContent,
  sidePanelContent,
  isSearchMode = false,
  usingSectionSearch = false,
  suspects,
}: {
  filters: MrFilterSetting[] | undefined;
  mappedConsolidated: MappedConsolidatedResources | undefined;
  isConsolidatedLoading: boolean;
  patientQueryParams?: PatientQueryParamProps | undefined;
  isImpersonating?: boolean;
  usingMetriportOnlyBundleFromConsolidated?: boolean;
  sidePanelContent?: React.ReactNode | undefined;
  setSidePanelContent: (content: React.ReactNode) => void;
  isSearchMode?: boolean;
  usingSectionSearch?: boolean;
  suspects?: Suspect[];
}) {
  const toast = useMetriportToast();
  const { sharedState } = useSharedContext();
  const [writingToEhrIds, setWritingToEhrIds] = useState<string[]>([]);
  const [failedToEhrIdsWithErrors, setFailedToEhrIdsWithErrors] = useState<
    { id: string; error: unknown }[]
  >([]);
  const [successToEhrIds, setSuccessToEhrIds] = useState<string[]>([]);
  const { handleDocumentClick, documentMenuComponent } = useDocumentMenu();
  const { filteredFhirSections } = useFhirSections();

  useEffect(() => {
    if (successToEhrIds.length > 0 && ehrActions) {
      toast.success({ title: `Successfully added to ${ehrActions.name}` });
    }
  }, [successToEhrIds]);

  useEffect(() => {
    const latest = failedToEhrIdsWithErrors[failedToEhrIdsWithErrors.length - 1];
    if (latest && ehrActions) {
      toast.error({
        title: `Error adding to ${ehrActions.name}${
          typeof latest.error === "string" ? `. ${latest.error}` : ""
        }`,
        duration: 3_000,
      });
    }
  }, [failedToEhrIdsWithErrors]);

  const ehrActions = useMemo(
    () =>
      sharedState.ehrDetails
        ? {
            ...sharedState.ehrDetails,
            writingIds: writingToEhrIds,
            failedIds: failedToEhrIdsWithErrors,
            successIds: successToEhrIds,
            setWritingIds: setWritingToEhrIds,
            setFailedIds: setFailedToEhrIdsWithErrors,
            setSuccessIds: setSuccessToEhrIds,
          }
        : undefined,
    [sharedState.ehrDetails, writingToEhrIds, failedToEhrIdsWithErrors, successToEhrIds]
  );

  const [fhirColumnDefs, fhirRowData] = useMemo(() => {
    const newFhirColumnDefs: Record<string, ColDef[]> = {};
    const newFhirRowData: Record<string, object[]> = {};
    filteredFhirSections.forEach(section => {
      if (section.generateTableData) {
        const { columnDefs, rowData } = section.generateTableData({
          bundle: mappedConsolidated,
          tableFilters: filters?.find(filter => filter.key === section.key),
          ehrActions,
          isMetriportUser: sharedState.isMetriportUser,
          suspects,
        });
        newFhirColumnDefs[section.key] = columnDefs;
        newFhirRowData[section.key] = rowData;
      }
    });
    return [newFhirColumnDefs, newFhirRowData];
  }, [
    ehrActions,
    mappedConsolidated,
    filters,
    filteredFhirSections,
    sharedState.isMetriportUser,
    suspects,
  ]);

  if (
    mappedConsolidated &&
    Object.keys(mappedConsolidated).length === 0 &&
    !isConsolidatedLoading
  ) {
    return (
      <Box>
        <NoPatientData
          message={
            usingMetriportOnlyBundleFromConsolidated ? "EHR is in sync" : "No Data Available"
          }
        />
      </Box>
    );
  }

  return (
    <Box>
      {documentMenuComponent}
      <Flex>
        <Skeleton
          flex={1}
          borderRadius={10}
          isLoaded={mappedConsolidated !== undefined && !isConsolidatedLoading}
        >
          <Sections
            fhirColumnDefs={fhirColumnDefs}
            fhirRowData={fhirRowData}
            isImpersonating={isImpersonating}
            fhirSections={filteredFhirSections}
            filters={filters}
            mappedConsolidated={mappedConsolidated}
            actions={{
              setSidePanelContent: (content: React.ReactNode) => {
                setSidePanelContent(content);
                const sidePanelContentId = document.getElementById("side-panel");
                sidePanelContentId?.scrollTo({ top: 0 });
              },
              handleDocumentClick,
            }}
            patientQueryParams={patientQueryParams}
            usingMetriportOnlyBundleFromConsolidated={usingMetriportOnlyBundleFromConsolidated}
            isSearchMode={isSearchMode}
            usingSectionSearch={usingSectionSearch}
          />
        </Skeleton>
        <SidePanel
          setSidePanelContent={() => {
            patientQueryParams?.clearPatientQueryParams();
            setSidePanelContent(undefined);
          }}
          showPanel={!!sidePanelContent}
        >
          {sidePanelContent}
        </SidePanel>
      </Flex>
    </Box>
  );
}

function Sections({
  fhirSections,
  fhirColumnDefs,
  fhirRowData,
  filters,
  mappedConsolidated,
  actions,
  patientQueryParams,
  isImpersonating,
  usingMetriportOnlyBundleFromConsolidated = false,
  isSearchMode = false,
  usingSectionSearch = false,
}: {
  fhirSections: FhirSections;
  fhirColumnDefs: Record<string, ColDef[]>;
  fhirRowData: Record<string, object[]>;
  filters: MrFilterSetting[] | undefined;
  mappedConsolidated: MappedConsolidatedResources | undefined;
  actions: FhirSectionActions;
  patientQueryParams?: PatientQueryParamProps | undefined;
  isImpersonating?: boolean;
  usingMetriportOnlyBundleFromConsolidated?: boolean;
  isSearchMode?: boolean;
  usingSectionSearch: boolean;
}) {
  let sections = fhirSections;
  if (filters) {
    const filteredSections = [];

    for (const filter of filters) {
      const { key } = filter;

      const section = sections.find(section => section.key === key);

      if (section) {
        filteredSections.push(section);
      }
    }

    sections = filteredSections;
  }

  const createCellStyleHandler =
    (sectionFilter: MrFilterSetting | undefined) => (params: CellClassParams) => {
      if (!params.data || !sectionFilter?.stringFilter) return undefined;

      const filterTerms = sectionFilter.stringFilter
        .split(",")
        .map((term: string) => term.toLowerCase().trim());

      const cellValues = Object.entries(params.data)
        .map(([key, value]) => {
          if (value === undefined) return undefined;
          if (key.toLowerCase().includes("id")) return undefined;
          return value?.toString().toLowerCase().trim();
        })
        .filter((value): value is string => value !== undefined);

      // Is there any cell that matches any filter?
      const cellMatchesFilter = filterTerms.some((term: string) =>
        cellValues.some(value => value.includes(term))
      );

      return cellMatchesFilter ? { backgroundColor: METRIPORT_PRIMARY_FADED } : undefined;
    };

  const createCellClickHandler =
    (PanelContent: React.ElementType) =>
    (sectionKey: string, { data }: { data: object }) => {
      const id = (data as { id: string }).id;
      if (id && typeof id === "string") {
        patientQueryParams?.setPatientQueryParams({
          sectionKey: sectionKey,
          resourceId: id,
        });
      }

      actions.setSidePanelContent(
        <PanelContent
          mappedConsolidated={mappedConsolidated}
          isImpersonating={isImpersonating}
          {...data}
        />
      );
    };

  return (
    <>
      {sections.map(section => {
        const sectionFilter = filters?.find(filter => filter.key === section.key);
        const columnDefs = fhirColumnDefs[section.key];
        const rowData = fhirRowData[section.key];
        if (!columnDefs || !rowData) return null;

        // New sections where the component has been registered
        if (section.component) {
          // If in search mode and no data, don't render the section
          if (isSearchMode && rowData.length === 0) {
            return null;
          }

          return (
            <section.component
              key={section.key}
              sectionId={section.key}
              sectionName={section.title}
              columnDefs={columnDefs}
              rowData={rowData}
              patientQueryParams={patientQueryParams}
              usingSectionSearch={usingSectionSearch}
              usingMetriportOnlyBundleFromConsolidated={usingMetriportOnlyBundleFromConsolidated}
              handleCellClick={createCellClickHandler(
                section.sidePanelContent ?? DefaultSidePanelContent
              )}
              handleCellStyle={createCellStyleHandler(sectionFilter)}
            />
          );
        } else {
          if (section.key === "documents") {
            return (
              <RawDocumentsSection
                key={section.key}
                sectionId={section.key}
                sectionName={section.title}
                columnDefs={columnDefs}
                rowData={rowData}
                patientQueryParams={patientQueryParams}
                usingSectionSearch={usingSectionSearch}
                handleCellClick={(sectionId, event) => {
                  if ("id" in event.data && "fileName" in event.data && "fileType" in event.data) {
                    actions.handleDocumentClick(
                      {
                        id: event.data.id as string,
                        fileName: event.data.fileName as string,
                        fileType: event.data.fileType as string,
                      },
                      event.event as unknown as React.PointerEvent<HTMLElement>
                    );
                    return;
                  }
                }}
                handleCellStyle={createCellStyleHandler(sectionFilter)}
              />
            );
          }

          throw new Error(`Section ${section.key} has no component registered`);
        }
      })}
    </>
  );
}

export function sectionsHaveData({ sectionCounts }: SectionCounts): boolean {
  return Object.values(sectionCounts).some(count => count > 0);
}
